import Layout from '../components/layout/Layout'
import { Link } from 'react-router-dom'
import { ArrowUpRight, X } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import Seo from '@/components/Seo'

type ExperienceItem = {
  id: string
  company: string
  role: string
  period: string
  href?: string
  opensDanvasModal?: boolean
  acquisitionNote?: string
}

const experiences: ExperienceItem[] = [
  {
    id: 'jelly-ai',
    company: 'Jelly AI',
    role: 'Founder & CTO',
    period: '2026–now',
    href: 'https://www.getjelly.com/'
  },
  {
    id: 'danvas',
    company: 'Danvas',
    role: 'CTO',
    period: '2022–2024',
    opensDanvasModal: true
  },
  {
    id: 'saatchiart',
    company: 'SaatchiArt',
    role: 'Head of Engineering',
    period: '2019–2022',
    href: 'https://saatchiart.com'
  },
  {
    id: 'leaf-group',
    company: 'Leaf Group',
    role: 'Engineer',
    period: '2015–2019',
    acquisitionNote: 'Acquired by Graham Holdings'
  }
]

const About = () => {
  const [showDanvasModal, setShowDanvasModal] = useState(false)

  return (
    <Dialog.Root open={showDanvasModal} onOpenChange={setShowDanvasModal}>
      <Seo
        title="Sergey Tsibel"
        description="Founder, CTO, and engineer exploring product design, software systems, and human movement."
        url="https://tsibel.com/"
        image="https://tsibel.com/1200x630.jpg"
        robots="index, follow, max-image-preview:large"
        googleBot="index, follow, max-image-preview:large"
      />
      <Layout>
        <div className="w-full max-w-4xl px-4 md:px-8 lg:px-12 py-12 space-y-6">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold text-foreground mb-6">Hello,</h1>
            <p className="text-foreground text-lg leading-relaxed text-justify">
              I explore the future of{' '}
              <Link
                to="/blog/chibatore"
                className="inline-block border-b border-transparent hover:border-dashed hover:border-foreground transition-all duration-300 pb-[2px]"
              >
                human body
              </Link>
              ,{' '}
              innovation, and philosophy. I build products, research ideas, and collaborate with startups and organizations to design solutions that are meaningful, accessible, and precise. I’m drawn to simplicity as a way to find clarity in complex problems.
            </p>
            <p className="mt-4 text-justify">
              Learn more about my software consulting company{' '}
              <a
                href="https://setolab.com"
                className="inline-flex items-center px-2 py-0.5 rounded-full text-foreground text-[10px] font-semibold border border-foreground hover:bg-foreground hover:text-white transition-colors duration-300"
                target="_blank"
                rel="noopener noreferrer"
              >
                SETO LAB
                <ArrowUpRight size={12} className="ml-1" />
              </a>
              {' '}or contact me directly via{' '}
              <a
                href="https://www.linkedin.com/in/sergey-tsibel-28334854/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground underline hover:text-foreground/80"
              >
                LinkedIn
              </a>
            </p>

            <div className="mt-16">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">Experience</h2>
              <div className="space-y-8">
                {experiences.map((experience) => (
                  <div key={experience.id} className="grid grid-cols-[180px_1fr] gap-x-8 items-baseline">
                    <p className={`text-lg font-bold ${experience.acquisitionNote ? 'group relative' : ''}`}>
                      {experience.opensDanvasModal ? (
                        <button
                          type="button"
                          onClick={() => setShowDanvasModal(true)}
                          className="bg-transparent p-0 text-left hover:underline cursor-pointer"
                        >
                          {experience.company}
                        </button>
                      ) : experience.href ? (
                        <a
                          href={experience.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {experience.company}
                        </a>
                      ) : (
                        <span className={experience.acquisitionNote ? 'hover:line-through cursor-default' : ''}>
                          {experience.company}
                        </span>
                      )}
                      {experience.acquisitionNote && (
                        <span className="absolute left-0 -bottom-5 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          {experience.acquisitionNote}
                        </span>
                      )}
                    </p>
                    <div>
                      <p className="text-lg">{experience.role}</p>
                      <p className="text-sm text-muted-foreground">{experience.period}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
          <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto focus:outline-none">
            <Dialog.Title className="sr-only">Danvas video</Dialog.Title>
            <Dialog.Description className="sr-only">
              Promotional video showcasing Danvas.
            </Dialog.Description>
            <div className="w-full max-w-2xl md:max-w-4xl lg:max-w-6xl relative flex items-center">
              <Dialog.Close asChild>
                <button
                  className="absolute top-4 right-4 p-2 text-white hover:text-white/80 transition-colors rounded-full bg-black/50 hover:bg-black/70 z-50"
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </Dialog.Close>
              <video
                src="https://assets.setolab.com/danvas-promo-video.mp4"
                className="w-full h-auto max-h-[calc(100vh-4rem)]"
                controls
              />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Layout>
    </Dialog.Root>
  )
}

export default About
